import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getGitHubCdnUrl } from "../../utils/githubMediaUtils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AboutSction = () => {
  const portfolioRef = useRef();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  
  // Use state to handle image URL and errors
  const [imageUrl, setImageUrl] = useState(getGitHubCdnUrl("photo/3.jpg"));
  const [imageError, setImageError] = useState(false);

  // Framer Motion animation controls for text, image, and button
  const controls = useAnimation();
  const imgControls = useAnimation();
  const buttonControls = useAnimation(); // Separate controls for the button

  const [ref, inView] = useInView({ threshold: 0.2 }); // Trigger when 20% of the component is in view
  const [textRef, textInView] = useInView({ threshold: 0.2 }); // Separate inView for text
  const [buttonRef, buttonInView] = useInView({ threshold: 0.2 }); // Separate inView for button

  // Image animation
  useEffect(() => {
    if (inView) {
      imgControls.start({
        scale: 1,
        opacity: 1,
        transition: { duration: 0.8 },
      });
    } else {
      imgControls.start({
        scale: 0.8,
        opacity: 0,
        transition: { duration: 0.8 },
      });
    }
  }, [inView, imgControls]);

  // Text animation
  useEffect(() => {
    if (textInView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 1 },
      });
    } else {
      controls.start({
        opacity: 0,
        y: 50,
        transition: { duration: 1 },
      });
    }
  }, [textInView, controls]);

  // Button animation
  useEffect(() => {
    if (buttonInView) {
      buttonControls.start("visible");
    } else {
      buttonControls.start("hidden");
    }
  }, [buttonInView, buttonControls]);

  const buttonVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 10,
        delay: 0.9,
      },
    },
    hover: {
      scale: 1.05,

      transition: { duration: 0.2 },

    },
  };

  const navigate = useNavigate();

  // Scroll to ServicesSection if returning from a detail page
  useEffect(() => {
    const handlePopState = () => {
      const fromPortfolio = localStorage.getItem("fromPortfolio");

      if (fromPortfolio) {
        portfolioRef.current.scrollIntoView({ behavior: "smooth" });
        localStorage.removeItem("fromPortfolio"); // Clear the flag after scrolling
      }
    };

    // Run the function once on component mount
    handlePopState();

    // Listen for back button (popstate) events
    window.addEventListener("popstate", handlePopState);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const HandleNavigate = (e) => {
    e.preventDefault();
    localStorage.setItem("fromPortfolio", "true"); // Set the flag in localStorage
    navigate("/Portfolio");
  };
  
  // Handle image error
  const handleImageError = () => {
    console.log("Image failed to load, using fallback");
    setImageError(true);
  };
  
  return (
    <div
      id="About"
      ref={portfolioRef}
      className="relative w-full h-full flex   bg-white   items-center justify-between mt-16 mb-16 px-2  "
    >
      <div className="container mx-auto flex gap-5 justify-between items-center ">
        <div ref={textRef} className="md:w-1/2 space-y-6">
          <motion.h1
            className="xl:text-8xl text-6xl  font-bold"
            initial={{ opacity: 0, y: 50 }}
            animate={controls} // Controls based on scrolling
          >
            {t("discover")} <br />
            <span className="text-primary">{t("our")}</span>{" "}
            <span className="text-black">{t("studio")}</span>
          </motion.h1>

          <motion.p
            className="lg:text-2xl  text-gray-500  leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={controls} // Controls based on scrolling
          >
            {t("about_subtitle_start")}
          </motion.p>

          <motion.p
            className="lg:text-2xl  text-gray-500  leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={controls} // Controls based on scrolling
          >
            {t("about_subtitle_end")}
          </motion.p>

          <div className="h-1 bg-white" />
          {/* Button with animation and width set to half of the text div */}
          <motion.button
            ref={buttonRef} // Track button visibility with useInView
            className="flex items-center justify-between  px-10 py-3 bg-primary text-lg text-black rounded-lg md:w-1/3 w-4/3"

            variants={buttonVariants}
            initial="hidden"
            animate={buttonControls} // Control based on scrolling
            whileHover="hover"
            onClick={HandleNavigate}
          >
            {t("portfolio")}
            <span className="mx-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5"
              >
                {isArabic ? (
                  // RTL Arrow Icon
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 8l-4 4m0 0l4 4m-4-4h18"
                  />
                ) : (
                  // LTR Arrow Icon
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                )}
              </svg>
            </span>
          </motion.button>
        </div>
        {/* Right Side Image */}
        <div className="md:flex hidden mt-8 md:mt-0 h-full">
          <motion.img
            ref={ref} // Add the ref to track the image visibility
            src={imageError ? "/images/placeholder.jpg" : imageUrl}
            alt="ARUBA Studio"
            className="rounded-lg shadow-lg w-[400px] h-1/3"
            initial={{ scale: 0.8, opacity: 0 }} // Initial state before scroll
            animate={imgControls} // Animate using Framer Motion's control
            onError={handleImageError} // Handle image loading errors
          />
        </div>
      </div>
    </div>
  );
};

export default AboutSction;
